import { ActionTypes } from '../actions';
import { applyArchivedStateToTableRows } from '../utils/itemArchive';
import type { ArchivedListMode } from '../utils/itemArchive';
import { updateItemInArray } from './utils';

interface ITableData {
    meta: any;
    data: any;
    error: any;
}


const initialState: any = {
    tables: [],
    meta: [],
    bulkDeleteInProgress: false,
    bulkDeleteCount: 0,
    loadingBySlug: {},
};




const tableReducer = (state = initialState, action: any) => {
    switch (action.type) {
        case ActionTypes.TABLE_REQUEST: {
            const tableKey = action.payload?.tableKey || action.payload?.slugName;
            if (tableKey == null || tableKey === '') {
                return state;
            }
            return {
                ...state,
                loadingBySlug: {
                    ...(state.loadingBySlug || {}),
                    [tableKey]: true
                },
            };
        }
        case ActionTypes.TABLE_META_SUCCESS:
            return {
                ...state,
                meta: updateItemInArray(state.meta, action.payload, 'tableKey'),
            };
        case ActionTypes.TABLE_SUCCESS: {
            const { slug, data, pagination, clearLoadingForKeys } = action.payload;
            const entry = { slug, data: data ?? [], ...(pagination && { pagination }) };
            const nextLoading = { ...(state.loadingBySlug || {}) };
            const keys =
                Array.isArray(clearLoadingForKeys) && clearLoadingForKeys.length > 0
                    ? clearLoadingForKeys.filter((k: unknown) => typeof k === 'string' && k.length > 0)
                    : slug != null && slug !== ''
                        ? [slug]
                        : [];
            for (const k of keys) {
                nextLoading[k as string] = false;
            }
            return {
                ...state,
                tables: updateItemInArray(state.tables, entry, 'slug'),
                loadingBySlug: nextLoading,
                error: null,
                currentSlug: typeof slug === 'string' && slug.length > 0 ? slug : state.currentSlug,
            };
        }
        case ActionTypes.TABLE_FAILURE: {
            const raw = action.payload;
            const payload =
                raw && typeof raw === 'object' && !Array.isArray(raw)
                    ? raw
                    : { message: String(raw ?? 'Unknown error') };
            const kTable = payload.tableKey;
            const kSlug = payload.slug;
            const nextLoading = { ...(state.loadingBySlug || {}) };
            if (kTable) nextLoading[kTable] = false;
            if (kSlug && kSlug !== kTable) nextLoading[kSlug] = false;
            if (!kTable && !kSlug) {
                Object.keys(nextLoading).forEach((k) => {
                    nextLoading[k] = false;
                });
            }
            return {
                ...state,
                error: payload,
                loadingBySlug: nextLoading,
            };
        }
        case ActionTypes.CLEAR_TABLE_ERROR:
            return {
                ...state,
                error: null,
            };
        case ActionTypes.CLEAR_TABLE_DATA:
            // Remove table data for the specified slug
            const slugToClear = action.payload?.slug;
            if (slugToClear) {
                return {
                    ...state,
                    tables: state.tables.filter((t: any) => t.slug !== slugToClear),
                };
            }
            // If no slug specified, clear all tables
            return {
                ...state,
                tables: [],
            };
        case ActionTypes.TABLE_SET_SORTING: {
            const slug = action.payload?.slug;
            if (!slug) return state;
            const sortValue = Array.isArray(action.payload.sorting)
                ? [...action.payload.sorting]
                : action.payload.sorting;
            const exists = state.tables.some((t: any) => t.slug === slug);
            if (!exists) {
                return {
                    ...state,
                    tables: [...state.tables, { slug, sorting: sortValue, data: [] }],
                };
            }
            return {
                ...state,
                tables: state.tables.map((t: any) =>
                    t.slug === slug ? { ...t, sorting: sortValue } : t
                ),
            };
        }
        case ActionTypes.DELETE_ITEMS_BULK: {
            const uids: string[] = Array.isArray(action.payload?.uids) ? action.payload.uids : [];
            // Optimistically drop the deleted rows from cached tables so they disappear
            // immediately instead of lingering until the next data reload. uids are globally
            // unique, so filtering every table's data is safe. A failed delete re-fetches
            // (see bulkDeleteInProgress effect in ComponentTable), restoring the rows.
            const deletedUidSet = new Set(uids);
            return {
                ...state,
                tables: deletedUidSet.size > 0
                    ? state.tables.map((t: any) =>
                        Array.isArray(t?.data)
                            ? { ...t, data: t.data.filter((item: any) => !deletedUidSet.has(item?.uid)) }
                            : t
                    )
                    : state.tables,
                bulkDeleteInProgress: true,
                bulkDeleteCount: uids.length,
            };
        }
        case ActionTypes.DELETE_ITEMS_BULK_SUCCESS:
        case ActionTypes.DELETE_ITEMS_BULK_ERROR:
            return {
                ...state,
                bulkDeleteInProgress: false,
                bulkDeleteCount: 0,
            };
        case ActionTypes.PATCH_TABLE_ITEMS_ARCHIVED: {
            const uids: string[] = Array.isArray(action.payload?.uids) ? action.payload.uids : [];
            const archived = action.payload?.archived === true;
            const listMode: ArchivedListMode =
                action.payload?.listMode === 'all' || action.payload?.listMode === 'only'
                    ? action.payload.listMode
                    : 'no';
            if (uids.length === 0) {
                return state;
            }
            return {
                ...state,
                tables: state.tables.map((t: any) =>
                    Array.isArray(t?.data)
                        ? {
                              ...t,
                              data: applyArchivedStateToTableRows(t.data, uids, archived, listMode),
                          }
                        : t
                ),
            };
        }
        case ActionTypes.LOGOUT:
            return {
                ...initialState,
                error: null,
                bulkDeleteInProgress: false,
                bulkDeleteCount: 0,
            };
        default:
            if (!action.type.toString().includes('@')) {
            }
            return state;
    }
}

export default tableReducer;

